<template>
  <div class="">
    <div class="flex flex-col space-y-4">
      <h2 class="text-2xl font-bold text-vulcan">{{ t("consent.heading") }}</h2>
      <SwitchGroup
        as="div"
        class="px-4 py-4 py-5 overflow-hidden bg-white shadow sm:p-6 sm:px-6 sm:rounded-md"
      >
        <SwitchLabel
          as="h3"
          class="text-lg font-medium leading-6 text-gray-900"
          passive
        >
          {{ t("consent.one.heading") }}
        </SwitchLabel>
        <div class="flex-col mt-2 sm:flex sm:items-start sm:justify-between">
          <div class="max-w-xl text-sm text-gray-500">
            <SwitchDescription>
              {{ t("consent.one.info") }}
            </SwitchDescription>
          </div>
          <div
            class="mt-5 space-x-4 sm:flex-shrink-0 sm:flex sm:flex-row sm:items-center"
          >
            <Switch
              v-model="switchOne"
              :class="[
                switchOne
                  ? 'bg-green-500 focus:ring-green-500'
                  : 'bg-gray-200 focus:ring-gray-200',
                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
              ]"
            >
              <span
                aria-hidden="true"
                :class="[
                  switchOne ? 'translate-x-5' : 'translate-x-0',
                  'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                ]"
              />
            </Switch>
            <p>{{ t("consent.check") }}</p>
          </div>
        </div>
      </SwitchGroup>
      <SwitchGroup
        as="div"
        class="px-4 py-4 py-5 overflow-hidden bg-white shadow sm:p-6 sm:px-6 sm:rounded-md"
      >
        <SwitchLabel
          as="h3"
          class="text-lg font-medium leading-6 text-gray-900"
          passive
        >
          {{ t("consent.two.heading") }}
          <span class="inline-flex items-center text-xs font-bold text-red-500">
            {{ t("consent.mandatory") }}
          </span>
        </SwitchLabel>
        <div class="flex-col mt-2 sm:flex sm:items-start sm:justify-between">
          <div class="max-w-xl text-sm text-gray-500">
            <SwitchDescription>
              {{ t("consent.two.info") }}
            </SwitchDescription>
          </div>
          <div
            class="mt-5 space-x-4 sm:flex-shrink-0 sm:flex sm:flex-row sm:items-center"
          >
            <Switch
              v-model="switchTwo"
              :class="[
                switchTwo
                  ? 'bg-green-500 focus:ring-green-500'
                  : 'bg-gray-200 focus:ring-gray-200',
                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
              ]"
            >
              <span
                aria-hidden="true"
                :class="[
                  switchTwo ? 'translate-x-5' : 'translate-x-0',
                  'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                ]"
              />
            </Switch>
            <p>{{ t("consent.check") }}</p>
          </div>
        </div>
      </SwitchGroup>
      <SwitchGroup
        as="div"
        class="px-4 py-4 py-5 overflow-hidden bg-white shadow sm:p-6 sm:px-6 sm:rounded-md"
      >
        <SwitchLabel
          as="h3"
          class="text-lg font-medium leading-6 text-gray-900"
          passive
        >
          {{ t("consent.three.heading") }}
          <span class="inline-flex items-center text-xs font-bold text-red-500">
            {{ t("consent.mandatory") }}
          </span>
        </SwitchLabel>
        <div class="flex-col mt-2 sm:flex sm:items-start sm:justify-between">
          <div class="max-w-xl text-sm text-gray-500">
            <SwitchDescription>
              {{ t("consent.three.info") }}
            </SwitchDescription>
          </div>
          <div
            class="mt-5 space-x-4 sm:flex-shrink-0 sm:flex sm:flex-row sm:items-center"
          >
            <Switch
              v-model="switchThree"
              :class="[
                switchThree
                  ? 'bg-green-500 focus:ring-green-500'
                  : 'bg-gray-200 focus:ring-gray-200',
                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
              ]"
            >
              <span
                aria-hidden="true"
                :class="[
                  switchThree ? 'translate-x-5' : 'translate-x-0',
                  'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                ]"
              />
            </Switch>
            <p>{{ t("consent.check") }}</p>
          </div>
        </div>
      </SwitchGroup>
    </div>
    <p class="py-4 text-sm font-bold text-red-500" v-if="showError">
      {{ t("consent.error") }}
    </p>
    <div class="flex flex-row justify-end p-4 space-x-4">
      <button
        @click="cancel"
        class="px-4 py-1 text-white bg-red-500 rounded-md"
      >
        {{ t("consent.cancel") }}
      </button>
      <button
        @click="agree"
        class="px-4 py-1 text-white bg-green-500 rounded-md"
      >
        {{ t("consent.button") }}
      </button>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import {
  Switch,
  SwitchDescription,
  SwitchGroup,
  SwitchLabel,
} from "@headlessui/vue";

import { useI18n } from "vue-i18n";

export default {
  components: {
    Switch,
    SwitchDescription,
    SwitchGroup,
    SwitchLabel,
  },
  props: {
    consent: {
      type: Object,
      default: { one: false, two: false, three: false },
    },
    id: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const switchOne = ref(props.consent.one);
    const switchTwo = ref(props.consent.two);
    const switchThree = ref(props.consent.three);
    const showError = ref(false);

    const isConsent = computed(() => {
      return switchTwo.value && switchThree.value;
    });

    const cancel = async () => {
        emit("close", false);
    };

    const agree = async () => {
      if (!isConsent.value) {
        showError.value = true;
        return true;
      }

      showError.value = false;
      const consent = {
        one: switchOne.value,
        two: switchTwo.value,
        three: switchThree.value,
      };

      try {
        await axios.patch(`/applications/${props.id}/consent`, {
          consent,
          is_consent: isConsent.value,
        });

        emit("close", true);
      } catch (e) {
          emit("close", false);
      }
    };

    return {
      t,
      showError,
      cancel,
      agree,
      switchOne,
      switchTwo,
      switchThree,
    };
  },
};
</script>
